import { Tooltip } from '@mui/material';
import React, { useCallback, useState } from 'react';

interface TruncationTooltipProps {
  /** What to show when the child is actually cut off. */
  title: React.ReactNode;
  children: React.ReactElement;
}

/**
 * HITL-1616 — the phase rule's second half.
 *
 * Every name in the centre pane is one line with an ellipsis, and carries a
 * tooltip ONLY when it is actually truncated. A tooltip on every row would mean
 * a popup follows the agent's cursor across a pane they are trying to read; a
 * tooltip on none would make an ellipsised name unreadable. `scrollWidth >
 * clientWidth` is the only honest test, and it can only be asked of a real DOM
 * node.
 *
 * Measured on hover rather than on render: the pane is resizable, so whether a
 * given name fits changes with the split and any value computed at render time
 * would be stale by the time it mattered.
 */
const TruncationTooltip = ({ title, children }: TruncationTooltipProps) => {
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const node = event.currentTarget;
    setIsTruncated(node.scrollWidth > node.clientWidth);
  }, []);

  return (
    <Tooltip
      title={isTruncated && title ? title : ''}
      arrow
      placement="top"
      disableInteractive
    >
      {React.cloneElement(children, { onMouseEnter: measure })}
    </Tooltip>
  );
};

export default TruncationTooltip;
